/**
 * HARUM (HAR-221): glue between the Hyper theme and Nova: Multi Currency Converter.
 *
 * Nova (Booster core, booster_currency.js) converts every `span.money` once the DOM is
 * ready, re-converts four more times at 800 ms intervals right after that ("lightning"
 * burst), and later only on its bundled jQuery `ajaxSuccess` event. Hyper never fires that
 * event: the cart drawer, cart page, quick view, bundles and quantity breaks all use native
 * fetch() and the Section Rendering API, so prices inserted later stayed in the store
 * currency. Nova tags converted nodes `.bacurr-money`, keeps the currency it shows in
 * sessionStorage `bacurr_user_cur` (a JSON string, e.g. "\"DKK\"") and reads that key back
 * on every conversion pass - at start-up in preference to its own IP lookup - writes every
 * price through its jQuery handle `window.bacurr$` (`.html()`), and exposes
 * `window.baCurr.refreshConversion()`.
 *
 * The currency a page shows is decided by the country in the header selector: Shopify's
 * country is mapped to a currency by snippets/harum-region-currency.liquid (single USD market,
 * so Shopify itself says USD for every country), the inline script in layout/theme.liquid
 * writes that code into `bacurr_user_cur` before first paint, and snippets/country-selector.liquid
 * renders "Denmark (DKK – Danish Krone)" for the button and every list item. Picking a country
 * submits Shopify's localization form as before; the next page carries the new currency.
 * Nova's own floating currency bar is not used (job 5).
 *
 * Jobs:
 *
 * 1. Refresh: when new `.money` nodes appear without Nova's tag, call refreshConversion()
 *    (debounced). Each node is marked `data-harum-cur-seen` first, so a node Nova refuses
 *    to convert can trigger at most one refresh - no loops.
 *
 * 2. Format. Nova's per-currency formats are hard-coded in its bundle and there is no
 *    dashboard setting for them. Several are unusable on their own: MYR is "RM189.97 MYR"
 *    (symbol AND code), DKK and IDR are a bare number, 18 currencies share a bare "$"
 *    (CAD, AUD, SGD, HKD, TWD, ARS, ...), "¥" is both CNY and JPY, "kr" is SEK/NOK/ISK,
 *    "Rs" is six currencies. normalizeText() applies, to Nova's output:
 *      - a trailing/leading ISO code is dropped        "RM189.97 MYR" -> "RM189.97"
 *      - a distinctive symbol stays as Nova prints it   "€19,99", "₩29,000", "RM189.97"
 *      - no symbol, or an ambiguous one ($, ¥, kr, Rs, a single letter), becomes
 *        "CODE amount"                                  "303,10" -> "DKK 303,10",
 *                                                       "$31.768,84" -> "ARS 31.768,84",
 *                                                       "¥3,300" -> "JPY 3,300"
 *    The store currency is never touched.
 *    Where it runs: we wrap `bacurr$.fn.html` the moment Nova assigns window.bacurr$ (an
 *    accessor on window catches the assignment; if Nova got there first we wrap the
 *    existing handle). The corrected text is then what Nova itself writes - on the first
 *    conversion, on each pass of its polling burst and on every refresh - so nothing is
 *    repainted afterwards. The earlier approach (rewriting the text from a
 *    MutationObserver) made Nova's burst flip "DKK 303,10" -> "303,10" -> "DKK 303,10"
 *    four times in the first seconds: the flicker reported on 3 Sep. That rewrite is kept
 *    only as a fallback for when the wrapper could not be installed.
 *
 * 3. Country selector. The server already rendered the right label for the current country;
 *    this script only steps in when reality differs: Nova absent or switched off (app embed
 *    disabled) -> the label goes back to the store currency at window load; Nova showing some
 *    other currency -> that currency's label, taken from the list item that carries it.
 *    `html.harum-nova-active` (Nova shows a non-store currency) reveals the "Card will be
 *    charged in USD - $x USD" line under the cart totals (sections/cart-drawer.liquid,
 *    sections/main-cart.liquid, harum-custom.css).
 *
 * 4. `html.harum-nova-pending` is set before first paint by the inline script in
 *    layout/theme.liquid when the page's currency is not the store currency; it keeps
 *    not-yet-converted prices invisible instead of flashing USD first. We drop it at window
 *    load once Nova has converted everything (or when Nova is not on the page); the inline
 *    3 s timer is the hard cap.
 *
 * 5. Nova's floating currency bar (bottom-left) is suppressed: `config.dont_show_selector`
 *    is a switch in Nova's core that skips building it (harum-custom.css hides it as well in
 *    case Nova built it before this script ran). Conversion does not depend on the bar.
 *
 * 6. First visit: Shopify does not move a new visitor to their own country by itself, so a
 *    Danish shopper would land on "United States (USD – US Dollar)". Once per session, and
 *    never after the shopper has picked a country themselves (localStorage
 *    `harum_region_manual`), we ask Shopify's own `browsing_context_suggestions.json` which
 *    country it detects for the visitor's IP. When it names another available country we
 *    update the selectors in place (flag, name, currency label, hidden input), switch Nova to
 *    that country's currency, and POST the localization form in the background so the next
 *    page renders that country server-side. This is the mechanism Shopify documents for
 *    localization discovery; nothing is redirected or reloaded.
 *
 * No-op when Nova is not loaded (app embed disabled) or shows the store currency.
 * window.harumCurrency exposes the pure formatter for test/currency-format.test.js.
 */
(function () {
  'use strict';

  if (window.harumCurrencyRefreshInstalled) return;
  window.harumCurrencyRefreshInstalled = true;

  var REFRESH_DEBOUNCE_MS = 150;
  var SYNC_DEBOUNCE_MS = 40;
  var NOVA_KEY = 'bacurr_user_cur';
  var AUTO_KEY = 'harum_region_auto'; /* sessionStorage: detection done for this session */
  var MANUAL_KEY = 'harum_region_manual'; /* localStorage: the shopper chose a country */
  var refreshTimer = null;
  var syncTimer = null;

  function storeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  }

  function storageGet(store, key) {
    try {
      return window[store].getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (e) {
      /* storage blocked; the feature degrades to a per-page decision */
    }
  }

  function clean(text) {
    return String(text || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* Nova keeps the currency it shows in sessionStorage as a JSON string, e.g. "\"DKK\"".
     Only trust it while Nova is actually running on this page, so a value left over from
     an earlier visit cannot relabel the selector once the app embed is switched off. */
  function shownCurrency() {
    var api = window.baCurr;
    if (!api || (api.config && api.config.enabled === false)) return null;
    var code = null;
    try {
      code = JSON.parse(storageGet('sessionStorage', NOVA_KEY));
    } catch (e) {
      code = null;
    }
    if (!code) {
      var chosen = document.querySelector('.bacurr-cur-block .ba-chosen');
      code = chosen ? chosen.textContent.trim() : null;
    }
    return code && /^[A-Z]{3}$/.test(code) ? code : null;
  }

  /* ---- 1. refresh ------------------------------------------------------------------- */

  function pendingNodes() {
    return document.querySelectorAll('.money:not(.bacurr-money):not([data-harum-cur-seen])');
  }

  function refresh() {
    refreshTimer = null;
    var nodes = pendingNodes();
    if (!nodes.length) return;
    for (var i = 0; i < nodes.length; i++) nodes[i].setAttribute('data-harum-cur-seen', '');

    var api = window.baCurr;
    if (!api || typeof api.refreshConversion !== 'function') return;
    try {
      api.refreshConversion();
    } catch (e) {
      /* Nova internals failed; leave the prices in the store currency. */
    }
  }

  function scheduleRefresh() {
    if (refreshTimer) return;
    refreshTimer = setTimeout(refresh, REFRESH_DEBOUNCE_MS);
  }

  /* ---- 2. format -------------------------------------------------------------------- */

  /* Symbols Nova prints for more than one currency (lower-cased). Single letters are
     treated the same way in normalizeText. */
  var AMBIGUOUS_SYMBOLS = { $: 1, '¥': 1, kr: 1, 'kr.': 1, rs: 1, 'rs.': 1 };

  /* Plain text in, plain text out. `code` is the ISO code Nova is showing. */
  function normalizeText(text, code) {
    if (typeof text !== 'string' || !code || !/^[A-Z]{3}$/.test(code) || code === storeCurrency()) return text;
    var t = text.replace(/\s+/g, ' ').trim();
    if (!t) return text;
    /* No lookbehind: Safari < 16.4 would throw at RegExp construction, inside Nova's loop. */
    var bare = t
      .replace(new RegExp('^' + code + '(?![A-Za-z0-9])\\s*'), '')
      .replace(new RegExp('(^|[^A-Za-z0-9])\\s*' + code + '$'), '$1')
      .trim();
    var m = bare.match(/\d[\d.,]*/);
    if (!m) return text;
    var num = m[0].replace(/[.,]+$/, '');
    var pre = bare.slice(0, m.index).trim();
    var post = bare.slice(m.index + m[0].length).trim();
    var symbol = (pre + ' ' + post).trim();
    var ambiguous = !symbol || AMBIGUOUS_SYMBOLS[symbol.toLowerCase()] === 1 || /^[A-Za-z]$/.test(symbol);
    if (ambiguous) return code + ' ' + num;
    return bare;
  }

  /* Nova's format strings carry HTML entities ("&#8361;{{amount}}"). Decode, normalize,
     re-escape; return the original string untouched when nothing changed or when it
     contains markup we did not expect. */
  function normalizeHtml(raw, code) {
    if (typeof raw !== 'string' || /[<>]/.test(raw)) return raw;
    var box = document.createElement('textarea');
    box.innerHTML = raw;
    var text = box.value;
    var out = normalizeText(text, code);
    if (out === text) return raw;
    return out.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  var wrapperInstalled = false;

  function isPriceTarget(el) {
    return !!(el && el.nodeType === 1 && typeof el.closest === 'function' && el.closest('.money'));
  }

  /* Wrap Nova's jQuery `.html(string)` so what Nova writes into span.money is already in
     the normalized form. Everything else (getter form, non-price targets) passes through. */
  function wrapNovaJQuery($) {
    if (!$ || !$.fn || typeof $.fn.html !== 'function') return false;
    if ($.fn.html.harumNovaWrapped) return true;
    var original = $.fn.html;
    var wrapped = function (value) {
      if (typeof value === 'string' && this.length && isPriceTarget(this[0])) {
        arguments[0] = normalizeHtml(value, shownCurrency());
      }
      return original.apply(this, arguments);
    };
    wrapped.harumNovaWrapped = true;
    $.fn.html = wrapped;
    return true;
  }

  function installWrapper() {
    if (window.bacurr$) {
      wrapperInstalled = wrapNovaJQuery(window.bacurr$);
      return;
    }
    /* Nova runs `window.bacurr$ = window.bacurr$ || jQuery` from a promise callback well
       before its first conversion; catch that assignment. */
    var current;
    try {
      Object.defineProperty(window, 'bacurr$', {
        configurable: true,
        enumerable: true,
        get: function () {
          return current;
        },
        set: function (value) {
          current = value;
          if (value) wrapperInstalled = wrapNovaJQuery(value) || wrapperInstalled;
        },
      });
    } catch (e) {
      /* Non-configurable global; the observer-driven fallback below still applies. */
    }
  }

  /* Fallback for when the wrapper is not in place: rewrite already-converted nodes.
     Idempotent, writes only when the text actually changes, and leaves nodes alone while
     Nova's hover popup (original price) is inside them. */
  function normalizePrices(code) {
    if (wrapperInstalled) return;
    var nodes = document.querySelectorAll('.bacurr-money');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.firstElementChild) continue;
      var text = el.textContent;
      var out = normalizeText(text, code);
      if (out !== text && out !== text.trim()) el.textContent = out;
    }
  }

  /* ---- 3. country selector ---------------------------------------------------------- */

  /* Nova's currency names, for a currency no country in the list maps to. `chosen_cur` is a
     JSON string in the inline config and an array once Nova has parsed it. */
  function novaCurrencyName(code) {
    var cfg = window.baCurr && window.baCurr.config;
    var list = cfg && cfg.chosen_cur;
    if (typeof list === 'string') {
      try {
        list = JSON.parse(list);
      } catch (e) {
        list = null;
      }
    }
    if (!Array.isArray(list)) return null;
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i][code]) return clean(String(list[i][code]).replace(/\s*\([A-Z]{3}\)\s*$/, ''));
    }
    return null;
  }

  /* "(DKK – Danish Krone)": the label the server rendered for the first country that shows
     `code`, else built from Nova's name, else the bare code. */
  function labelFor(code) {
    var rendered = document.querySelector(
      '[data-disclosure-option][data-harum-currency="' + code + '"] .localization-form__currency'
    );
    if (rendered) return clean(rendered.textContent);
    var name = novaCurrencyName(code);
    return name ? '(' + code + ' – ' + name + ')' : '(' + code + ')';
  }

  function updateSelectorLabels(shown) {
    var store = storeCurrency();
    var active = !!shown && shown !== store;
    document.documentElement.classList.toggle('harum-nova-active', active);
    var spans = document.querySelectorAll('[data-harum-localization-currency]');
    for (var i = 0; i < spans.length; i++) {
      var span = spans[i];
      if (!span.hasAttribute('data-harum-original')) span.setAttribute('data-harum-original', clean(span.textContent));
      var region = span.getAttribute('data-harum-currency');
      var label;
      if (shown) {
        label = shown === region ? span.getAttribute('data-harum-original') : labelFor(shown);
      } else if (document.readyState === 'complete') {
        /* Nova absent or switched off: prices are in the store currency. */
        label = region === store ? span.getAttribute('data-harum-original') : span.getAttribute('data-harum-store-label') || labelFor(store);
      } else {
        continue; /* Nova not there yet; keep the server's label rather than flip it twice */
      }
      if (label && clean(span.textContent) !== label) span.textContent = label;
    }
  }

  /* Make every country selector on the page show `handle` (ISO country code) as the current
     country, copying flag, name and currency label from its own list item, and set the
     hidden input so a later submit carries it. Returns the matching list item of the first
     selector, or null when no selector lists that country. */
  function applyCountry(handle) {
    var found = null;
    var forms = document.querySelectorAll('localization-form');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var input = form.querySelector('input[name="country_code"]');
      var option = form.querySelector('[data-disclosure-option][data-value="' + handle + '"]');
      if (!input || !option) continue;
      input.value = handle;

      var flag = form.querySelector('[data-harum-localization-flag]');
      var optionFlag = option.querySelector('.f-country-flags');
      if (flag && optionFlag) {
        flag.className = optionFlag.className;
        flag.setAttribute('style', optionFlag.getAttribute('style') || '');
      }
      var name = form.querySelector('[data-harum-localization-country]');
      var optionName = option.getAttribute('data-harum-country-name');
      if (name && optionName) name.textContent = optionName;

      var currency = form.querySelector('[data-harum-localization-currency]');
      var optionCurrency = option.querySelector('.localization-form__currency');
      if (currency) {
        currency.setAttribute('data-harum-currency', option.getAttribute('data-harum-currency') || '');
        if (optionCurrency) {
          currency.textContent = clean(optionCurrency.textContent);
          currency.setAttribute('data-harum-original', clean(optionCurrency.textContent));
        }
      }

      var options = form.querySelectorAll('[data-disclosure-option]');
      for (var j = 0; j < options.length; j++) {
        var isCurrent = options[j].getAttribute('data-value') === handle;
        if (isCurrent) options[j].setAttribute('aria-current', 'true');
        else options[j].removeAttribute('aria-current');
        var item = options[j].parentElement;
        if (item && item.classList) item.classList.toggle('disclosure-list__item--current', isCurrent);
      }
      found = found || option;
    }
    return found;
  }

  /* Point Nova at `code` and re-run its conversion (every pass re-reads the stored key). */
  function setNovaCurrency(code) {
    storageSet('sessionStorage', NOVA_KEY, JSON.stringify(code));
    var api = window.baCurr;
    if (api && typeof api.refreshConversion === 'function') {
      try {
        api.refreshConversion();
      } catch (e) {
        /* Nova internals failed; the label still follows the stored code. */
      }
    }
    scheduleSync();
  }

  /* ---- 4. pre-paint hide ------------------------------------------------------------ */

  /* Only converted prices are visible while the class is on, so it costs nothing to keep it
     until window load: prices the theme's JS inserts after Nova's first pass (quantity
     breaks, sticky bar) then stay hidden for the ~150 ms until refresh() converts them,
     instead of flashing USD. The inline 3 s timer in theme.liquid is the hard cap. */
  function settlePending() {
    var html = document.documentElement;
    if (!html.classList.contains('harum-nova-pending') || document.readyState !== 'complete') return;
    if (!window.baCurr || !document.querySelector('.money:not(.bacurr-money)')) {
      html.classList.remove('harum-nova-pending');
    }
  }

  /* ---- 5. Nova's own currency bar --------------------------------------------------- */

  /* Read by Nova's core before it builds the bar and on every re-render; harmless later. */
  function hideNovaBar() {
    var api = window.baCurr;
    if (api && api.config && typeof api.config === 'object') api.config.dont_show_selector = true;
  }

  /* ---- 6. first-visit region detection ---------------------------------------------- */

  function postLocalization(form, handle) {
    if (!form || typeof window.FormData !== 'function') return Promise.resolve(false);
    var data = new FormData(form);
    data.set('country_code', handle);
    /* redirect: 'manual' - the 302 back to return_to still sets Shopify's country cookie,
       and we do not download the redirected page just to throw it away. */
    return window
      .fetch(form.action, { method: 'POST', body: data, credentials: 'same-origin', redirect: 'manual' })
      .then(function (response) {
        return response.ok || response.type === 'opaqueredirect';
      })
      .catch(function () {
        return false;
      });
  }

  function detectRegion() {
    var input = document.querySelector('localization-form input[name="country_code"]');
    if (!input || typeof window.fetch !== 'function') return;
    if (storageGet('sessionStorage', AUTO_KEY) || storageGet('localStorage', MANUAL_KEY)) return;
    var current = (window.Shopify && window.Shopify.country) || input.value;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url =
      root +
      'browsing_context_suggestions.json?country%5Benabled%5D=true&country%5Bexclude%5D=' +
      encodeURIComponent(current);
    window
      .fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        var suggestion = data && data.suggestions && data.suggestions[0];
        var country = suggestion && suggestion.parts && suggestion.parts.country;
        var handle = country && typeof country.handle === 'string' ? country.handle.toUpperCase() : null;
        if (!handle || handle === current) {
          storageSet('sessionStorage', AUTO_KEY, '1');
          return null;
        }
        var option = applyCountry(handle);
        if (!option) {
          storageSet('sessionStorage', AUTO_KEY, '1');
          return null;
        }
        var code = option.getAttribute('data-harum-currency');
        if (code && /^[A-Z]{3}$/.test(code)) setNovaCurrency(code);
        return postLocalization(input.form, handle).then(function (ok) {
          if (ok) storageSet('sessionStorage', AUTO_KEY, '1');
        });
      })
      .catch(function () {
        /* offline, blocked, or an unexpected payload: stay on the server's country */
      });
  }

  /* A country the shopper picked themselves wins over detection, in this and later visits. */
  function rememberManualChoice(event) {
    var target = event.target;
    var option = target && typeof target.closest === 'function' ? target.closest('[data-disclosure-option]') : null;
    if (!option) return;
    var form = option.closest('localization-form');
    if (!form || !form.querySelector('input[name="country_code"]')) return;
    storageSet('localStorage', MANUAL_KEY, '1');
  }

  /* ---- wiring ----------------------------------------------------------------------- */

  function sync() {
    syncTimer = null;
    hideNovaBar();
    var code = shownCurrency();
    if (code && code !== storeCurrency()) normalizePrices(code);
    updateSelectorLabels(code);
    settlePending();
  }

  function scheduleSync() {
    if (syncTimer) return;
    syncTimer = setTimeout(sync, SYNC_DEBOUNCE_MS);
  }

  function start() {
    hideNovaBar();
    new MutationObserver(function (mutations) {
      var added = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          added = true;
          break;
        }
      }
      if (added) scheduleRefresh();
      scheduleSync();
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
    document.addEventListener('click', rememberManualChoice, true);
    scheduleRefresh();
    scheduleSync();
    detectRegion();
  }

  installWrapper();
  window.harumCurrency = { normalizeText: normalizeText, normalizeHtml: normalizeHtml, labelFor: labelFor };
  window.addEventListener('load', function () {
    settlePending();
    scheduleSync();
  });

  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
